import React, { useState } from 'react';
import {
  Box, Flex, Heading, Text, VStack, HStack, Divider, Button, FormControl,
  FormLabel, Input, useToast, Icon, Textarea, Card, CardBody, InputGroup,
  InputLeftElement, Avatar, Link, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  Menu, MenuButton, MenuList, MenuItem, Portal
} from '@chakra-ui/react';
import { 
  FiSettings, FiCpu, FiPlus, FiChevronDown, FiGlobe, 
  FiSave, FiZap, FiTarget, FiUsers, FiMail, FiImage, FiTrash2 
} from 'react-icons/fi';

const WorkspaceSettings = () => {
  const toast = useToast();
  
  // --- Navigation State ---
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'context'
  const [selectedWorkspace, setSelectedWorkspace] = useState('gillyreach');

  // --- Form States ---
  const [isGenerating, setIsGenerating] = useState(false);
  const [companyUrl, setCompanyUrl] = useState('');
  const [workspaceData, setWorkspaceData] = useState({
    name: 'gillyreach',
    billingEmail: 'sayan@gillyreach.io',
    description: 'GillyReach is an AI-powered B2B prospecting platform...',
    icp: 'Sales teams within mid-sized to large enterprises...',
    personas: '1. SDRs looking for efficiency\n2. Sales Ops Managers'
  });

  // --- AI Generation Logic ---
  const handleGenerateAI = () => {
    if (!companyUrl) return;
    setIsGenerating(true);
    setTimeout(() => {
      setWorkspaceData({
        ...workspaceData,
        description: "AI Generated: This is a high-growth SaaS platform focusing on...",
        icp: "Mid-market software companies in the EMEA region...",
        personas: "1. VP of Sales\n2. Growth Marketers"
      });
      setIsGenerating(false);
      toast({ title: "Business context generated!", status: "success" });
    }, 2000);
  };

  // --- Styles ---
  const sidebarWidth = "280px";
  const bgMain = "#0F1219"; 
  const bgCard = "#1A202C"; // Matches your Profile SS
  const accentBlue = "rgba(65, 105, 225, 0.8)";
  const borderColor = "gray.700";

  return (
    <Flex h="100vh" bg={bgMain} color="white">
      
      {/* 1. LEFT SIDEBAR (Workspace Navigation) */}
      <Box 
        w={sidebarWidth} 
        borderRight="1px solid" 
        borderColor={borderColor} 
        p={6} 
        display="flex" 
        flexDirection="column"
      >
        {/* Workspace Switcher Header */}
        <Menu>
          <MenuButton 
            as={Button} 
            variant="ghost" 
            w="full" 
            justifyContent="space-between" 
            rightIcon={<FiChevronDown />}
            px={2}
            mb={8}
            _hover={{ bg: "whiteAlpha.100" }}
          >
            <HStack spacing={3}>
              <Avatar size="xs" name={selectedWorkspace} bg={accentBlue} />
              <Text fontWeight="bold" fontSize="md">{selectedWorkspace}</Text>
            </HStack>
          </MenuButton>
          <Portal>
            <MenuList bg={bgCard} borderColor={borderColor} color="white">
              <MenuItem bg={bgCard} _hover={{ bg: "whiteAlpha.200" }} onClick={() => setSelectedWorkspace('gillyreach')}>gillyreach</MenuItem>
              <MenuItem bg={bgCard} _hover={{ bg: "whiteAlpha.200" }} onClick={() => setSelectedWorkspace('acme-corp')}>Acme Corp</MenuItem>
              <Divider my={2} borderColor="gray.600" />
              <MenuItem bg={bgCard} color="blue.300" icon={<FiPlus />}>Create Workspace</MenuItem>
            </MenuList>
          </Portal>
        </Menu>

        <Text fontSize="xs" fontWeight="bold" color="gray.500" mb={4} textTransform="uppercase">
          Workspace Settings
        </Text>

        <VStack spacing={1} align="stretch">
          <NavItem 
            icon={FiSettings} 
            label="Workspace details" 
            isActive={activeTab === 'details'} 
            onClick={() => setActiveTab('details')} 
          />
          <NavItem 
            icon={FiCpu} 
            label="Business context" 
            isActive={activeTab === 'context'} 
            onClick={() => setActiveTab('context')} 
          />
        </VStack>

        <Divider my={6} borderColor={borderColor} />

        {/* Support/Other links can go here */}
      </Box>

      {/* 2. RIGHT CONTENT AREA */}
      <Box flex="1" overflowY="auto" p={10}>
        <Flex maxW="4xl" direction="column" mx="auto">
          
          {/* Header Row */}
          <Flex justify="space-between" align="center" mb={10}>
            <VStack align="start" spacing={1}>
              <Heading size="lg">
                {activeTab === 'details' ? 'Workspace settings' : 'Business context'}
              </Heading>
              <Text color="gray.400" fontSize="sm">
                {activeTab === 'details' 
                  ? 'Manage your workspace identity and billing preferences.' 
                  : 'Feed the AI with your company goals to improve outreach quality.'}
              </Text>
            </VStack>
            <Button 
              leftIcon={<FiSave />} 
              bg={accentBlue} 
              _hover={{ bg: "blue.500" }}
              size="md"
              px={8}
              onClick={() => toast({ title: "Saved successfully", status: "success" })}
            >
              Save Changes
            </Button>
          </Flex>

          {/* FORM AREA */}
          <Box bg={bgCard} borderRadius="xl" p={8} borderWidth="1px" borderColor={borderColor}>
            
            {activeTab === 'details' ? (
              /* --- WORKSPACE DETAILS FORM --- */
              <VStack spacing={8} align="stretch">
                <HStack spacing={6} align="center">
                  <Box p={4} borderRadius="full" border="2px dashed" borderColor="gray.600">
                    <Icon as={FiImage} boxSize={8} color="gray.500" />
                  </Box>
                  <HStack spacing={3}>
                    <Button size="sm" colorScheme="blue">Upload new picture</Button>
                    <Button size="sm" variant="ghost" color="red.400" leftIcon={<FiTrash2 />}>Delete</Button>
                  </HStack>
                </HStack>

                <Divider borderColor="gray.700" />

                <FormControl>
                  <FormLabel fontWeight="bold">Workspace name</FormLabel>
                  <Text fontSize="xs" color="gray.500" mb={2}>A human-friendly label for your workspace.</Text>
                  <InputGroup>
                    <InputLeftElement children={<FiSettings color="gray" />} />
                    <Input 
                      value={workspaceData.name} 
                      bg={bgMain} 
                      borderColor="gray.600" 
                      onChange={(e) => setWorkspaceData({...workspaceData, name: e.target.value})}
                    />
                  </InputGroup>
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold">Billing email</FormLabel>
                  <Text fontSize="xs" color="gray.500" mb={2}>The email address to use for all billing related emails.</Text>
                  <InputGroup>
                    <InputLeftElement children={<FiMail color="gray" />} />
                    <Input 
                      value={workspaceData.billingEmail} 
                      bg={bgMain} 
                      borderColor="gray.600"
                      onChange={(e) => setWorkspaceData({...workspaceData, billingEmail: e.target.value})}
                    />
                  </InputGroup>
                </FormControl>
              </VStack>

            ) : (
              /* --- BUSINESS CONTEXT FORM --- */
              <VStack spacing={8} align="stretch">
                
                {/* AI Generator Section */}
                <Card variant="outline" borderColor="blue.500" bg="blue.900" p={4}>
                  <VStack align="start" spacing={4}>
                    <HStack>
                      <Icon as={FiZap} color="yellow.400" />
                      <Text fontWeight="bold" fontSize="sm">Generate business context with AI</Text>
                    </HStack>
                    <Text fontSize="xs" color="blue.200">
                      Let Sculptor automatically research your company domain & generate your business context.
                    </Text>
                    <HStack w="full">
                      <Input 
                        placeholder="example.com" 
                        bg={bgMain} 
                        borderColor="blue.400" 
                        value={companyUrl}
                        onChange={(e) => setCompanyUrl(e.target.value)}
                      />
                      <Button 
                        bg={accentBlue} 
                        isLoading={isGenerating} 
                        onClick={handleGenerateAI}
                        px={8}
                        leftIcon={<FiCpu />}
                      >
                        Generate
                      </Button>
                    </HStack>
                  </VStack>
                </Card>

                <FormControl>
                  <FormLabel fontWeight="bold">Company description</FormLabel>
                  <Textarea 
                    h="120px" 
                    bg={bgMain} 
                    borderColor="gray.600" 
                    value={workspaceData.description}
                    onChange={(e) => setWorkspaceData({...workspaceData, description: e.target.value})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold">Ideal customer profile</FormLabel>
                  <Textarea 
                    h="120px" 
                    bg={bgMain} 
                    borderColor="gray.600" 
                    value={workspaceData.icp}
                    onChange={(e) => setWorkspaceData({...workspaceData, icp: e.target.value})}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="bold">Ideal buyer personas</FormLabel>
                  <Textarea 
                    h="120px" 
                    bg={bgMain} 
                    borderColor="gray.600" 
                    value={workspaceData.personas}
                    onChange={(e) => setWorkspaceData({...workspaceData, personas: e.target.value})}
                  />
                </FormControl>
              </VStack>
            )}
          </Box>
        </Flex>
      </Box>
    </Flex>
  );
};

// --- Sub-component for Sidebar Items ---
const NavItem = ({ icon, label, isActive, onClick }) => {
  const activeColor = "white";
  const inactiveColor = "gray.500";
  const activeBg = "rgba(65, 105, 225, 0.15)";

  return (
    <Button
      variant="ghost"
      w="full"
      justifyContent="flex-start"
      leftIcon={<Icon as={icon} color={isActive ? "blue.300" : inactiveColor} />}
      onClick={onClick}
      color={isActive ? activeColor : inactiveColor}
      bg={isActive ? activeBg : "transparent"}
      _hover={{ bg: isActive ? activeBg : "whiteAlpha.100", color: "white" }}
      borderRadius="md"
      fontWeight={isActive ? "bold" : "medium"}
    >
      {label}
    </Button>
  );
};

export default WorkspaceSettings;